import { Button, Col, Pagination, Row, Space, Spin, Tabs, Tag, Typography, Modal } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteFilled,
  CheckCircleOutlined,
  StopOutlined,
  PauseCircleOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getIsLoading,
  getModalVisibility,
  getPageMeta,
  getServicePrincipalRoles,
  getServicePrincipals,
  ModalName,
  ServicePrincipalActions,
} from '~/Store/ServicePrincipal'
import { getLeftSiderCollapsed } from '~/Store/Layout/selectors'
import { getUserOrganizations } from '~/Store/User/selectors'
import { ScrollBar } from '~/Components/ScrollBar'
import { NoData } from '~/Components/NoData'
import { Utils } from '~/Utils'
import { EditServicePrincipal } from './EditServicePrincipal'
import { ServicePrincipalDetail } from './ServicePrincipalDetail'
import {
  IServicePrincipal,
  ServicePrincipalStatus,
} from '~/Services/ServicePrincipal.interface'

const { confirm } = Modal

const statusTag = (status: ServicePrincipalStatus, t: (key: string) => string) => {
  switch (status) {
    case ServicePrincipalStatus.Active:
      return <Tag color="green">{t('admin.spStatusActive')}</Tag>
    case ServicePrincipalStatus.Suspended:
      return <Tag color="orange">{t('admin.spStatusSuspended')}</Tag>
    default:
      return <Tag color="red">{t('admin.spStatusInactive')}</Tag>
  }
}

const ServicePrincipalRow = ({
  sp,
  organizationName,
  onOpenDetail,
  onEdit,
  onDelete,
  onActivate,
  onInactivate,
  onSuspend,
}: {
  sp: IServicePrincipal
  organizationName: string
  onOpenDetail: (item: IServicePrincipal) => void
  onEdit: (item: IServicePrincipal) => void
  onDelete: (item: IServicePrincipal) => void
  onActivate: (item: IServicePrincipal) => void
  onInactivate: (item: IServicePrincipal) => void
  onSuspend: (item: IServicePrincipal) => void
}) => {
  const { t } = useTranslation()
  const roles = useAppSelector(getServicePrincipalRoles(sp.id))
  const isActive = sp.status === ServicePrincipalStatus.Active

  return (
    <div
      className="listItemBordered"
      style={{ cursor: 'pointer' }}
      onClick={() => onOpenDetail(sp)}
    >
      <Row gutter={[8, 8]} align="middle" wrap={false}>
        <Col flex="1 1 160px">{sp.name}</Col>
        <Col flex="1 1 160px">{organizationName}</Col>
        <Col flex="0 0 100px">{statusTag(sp.status, t)}</Col>
        <Col flex="1 1 160px">
          {(roles ?? []).map(role => (
            <Tag key={role.roleId}>{role.roleName}</Tag>
          ))}
        </Col>
        <Col flex="0 0 140px">{Utils.toLocaleDateTimeString(sp.createdAt)}</Col>
        <Col flex="0 0 120px" style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
          {isActive ? (
            <Space size="middle">
              <EditOutlined
                title={t('admin.editServicePrincipal')}
                style={{ color: '#1890ff' }}
                onClick={evt => {
                  evt.stopPropagation()
                  onEdit(sp)
                }}
              />
              <PauseCircleOutlined
                title={t('admin.spSuspend')}
                style={{ color: '#fa8c16' }}
                onClick={evt => {
                  evt.stopPropagation()
                  onSuspend(sp)
                }}
              />
              <StopOutlined
                title={t('admin.spInactivate')}
                style={{ color: '#8c8c8c' }}
                onClick={evt => {
                  evt.stopPropagation()
                  onInactivate(sp)
                }}
              />
              <DeleteFilled
                title={t('admin.delete')}
                style={{ color: 'red' }}
                onClick={evt => {
                  evt.stopPropagation()
                  onDelete(sp)
                }}
              />
            </Space>
          ) : (
            <CheckCircleOutlined
              title={t('admin.spActivate')}
              style={{ color: '#52c41a' }}
              onClick={evt => {
                evt.stopPropagation()
                onActivate(sp)
              }}
            />
          )}
        </Col>
      </Row>
    </div>
  )
}

export const ServicePrincipals = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const servicePrincipals = useAppSelector(getServicePrincipals)
  const organizations = useAppSelector(getUserOrganizations)
  const pageMeta = useAppSelector(getPageMeta)
  const isLoading = useAppSelector(getIsLoading)
  const leftMenuCollapsed = useAppSelector(getLeftSiderCollapsed)
  const editModal = useAppSelector(getModalVisibility(ModalName.ServicePrincipal))
  const detailVisible = useAppSelector(
    getModalVisibility(ModalName.ServicePrincipalDetail),
  )
  const [statusTab, setStatusTab] = useState<ServicePrincipalStatus>(
    ServicePrincipalStatus.Active,
  )

  useEffect(() => {
    dispatch(
      ServicePrincipalActions.getServicePrincipals({ status: statusTab }),
    )
  }, [])

  useEffect(() => {
    servicePrincipals.forEach(sp => {
      dispatch(ServicePrincipalActions.getServicePrincipalRoles(sp.id))
    })
  }, [servicePrincipals])

  // The list is already scoped to the current Organization, but rows carry
  // their own `organizationId` — resolve it to a company name where the user
  // is a member, and fall back to the raw id for anything else.
  const organizationName = (organizationId?: string | null) => {
    if (!organizationId) return '-'
    const org = organizations.find(o => o.guid === organizationId)
    return org?.companyName || organizationId
  }

  const changePage = (_page: number, _pageSize?: number) => {
    dispatch(
      ServicePrincipalActions.getServicePrincipals({
        status: statusTab,
        page: _page,
        pageSize: _pageSize,
      }),
    )
  }

  const changeStatusTab = (key: string) => {
    const status = key as ServicePrincipalStatus
    setStatusTab(status)
    dispatch(
      ServicePrincipalActions.getServicePrincipals({ status, page: 1 }),
    )
  }

  const newClick = () => {
    dispatch(ServicePrincipalActions.setServicePrincipalSelected(undefined))
    dispatch(
      ServicePrincipalActions.setModalVisibility({
        name: ModalName.ServicePrincipal,
        visible: true,
      }),
    )
  }

  const editClick = (item: IServicePrincipal) => {
    dispatch(ServicePrincipalActions.setServicePrincipalSelected(item))
    dispatch(
      ServicePrincipalActions.setModalVisibility({
        name: ModalName.ServicePrincipal,
        visible: true,
      }),
    )
  }

  const deleteClick = (item: IServicePrincipal) => {
    confirm({
      title: t('admin.confirmDeleteServicePrincipal'),
      icon: <ExclamationCircleFilled />,
      content: <div>{t('admin.name')}: {item.name}</div>,
      okText: t('admin.delete'),
      okType: 'danger',
      cancelText: t('cancel'),
      onOk() {
        dispatch(ServicePrincipalActions.deleteServicePrincipal(item.id))
      },
    })
  }

  const changeStatusClick = (
    item: IServicePrincipal,
    status: ServicePrincipalStatus,
    confirmTitleKey: string,
    okTextKey: string,
  ) => {
    confirm({
      title: t(confirmTitleKey),
      icon: <ExclamationCircleFilled />,
      content: <div>{t('admin.name')}: {item.name}</div>,
      okText: t(okTextKey),
      cancelText: t('cancel'),
      onOk() {
        dispatch(
          ServicePrincipalActions.updateServicePrincipal({ id: item.id, status }),
        )
      },
    })
  }

  const activateClick = (item: IServicePrincipal) =>
    changeStatusClick(
      item,
      ServicePrincipalStatus.Active,
      'admin.confirmActivateServicePrincipal',
      'admin.spActivate',
    )

  const inactivateClick = (item: IServicePrincipal) =>
    changeStatusClick(
      item,
      ServicePrincipalStatus.Deleted,
      'admin.confirmInactivateServicePrincipal',
      'admin.spInactivate',
    )

  const suspendClick = (item: IServicePrincipal) =>
    changeStatusClick(
      item,
      ServicePrincipalStatus.Suspended,
      'admin.confirmSuspendServicePrincipal',
      'admin.spSuspend',
    )

  const detailClick = (item: IServicePrincipal) => {
    dispatch(ServicePrincipalActions.setServicePrincipalSelected(item))
    dispatch(ServicePrincipalActions.getServicePrincipalDetail(item.id))
    dispatch(
      ServicePrincipalActions.setModalVisibility({
        name: ModalName.ServicePrincipalDetail,
        visible: true,
      }),
    )
  }

  return (
    <div>
      {editModal && <EditServicePrincipal />}
      {detailVisible && <ServicePrincipalDetail />}
      <Row align="middle" style={{ marginBottom: '8px' }}>
        <Typography.Title style={{ flex: 1, margin: 0 }} level={4}>
          {t('admin.servicePrincipals')}
        </Typography.Title>
        <Button type="primary" icon={<PlusCircleOutlined />} onClick={newClick}>
          {t('admin.newServicePrincipal')}
        </Button>
      </Row>
      <Tabs activeKey={statusTab} onChange={changeStatusTab}>
        <Tabs.TabPane tab={t('admin.spStatusActive')} key={ServicePrincipalStatus.Active} />
        <Tabs.TabPane
          tab={t('admin.spStatusInactive')}
          key={ServicePrincipalStatus.Deleted}
        />
        <Tabs.TabPane
          tab={t('admin.spStatusSuspended')}
          key={ServicePrincipalStatus.Suspended}
        />
      </Tabs>
      <div className="listItemBordered" style={{ fontWeight: 'bold' }}>
        <Row gutter={[8, 8]} wrap={false}>
          <Col flex="1 1 160px">{t('admin.name')}</Col>
          <Col flex="1 1 160px">{t('admin.spOrganization')}</Col>
          <Col flex="0 0 100px">{t('admin.status')}</Col>
          <Col flex="1 1 160px">{t('admin.spRoles')}</Col>
          <Col flex="0 0 140px">{t('admin.createdAt')}</Col>
          <Col flex="0 0 120px" />
        </Row>
      </div>
      <Spin spinning={isLoading}>
        <ScrollBar
          style={{
            paddingRight: '16px',
            marginRight: '-16px',
            height: 'calc(100vh - 330px)',
          }}
        >
          {servicePrincipals.length > 0 ? (
            servicePrincipals.map(sp => (
              <ServicePrincipalRow
                key={sp.id}
                sp={sp}
                organizationName={organizationName(sp.organizationId)}
                onOpenDetail={detailClick}
                onEdit={editClick}
                onDelete={deleteClick}
                onActivate={activateClick}
                onInactivate={inactivateClick}
                onSuspend={suspendClick}
              />
            ))
          ) : (
            <NoData content={t('admin.noData')} />
          )}
        </ScrollBar>
      </Spin>
      {pageMeta && (
        <div
          className="paginationBottom"
          style={{
            width: `calc(100% - ${leftMenuCollapsed ? '146' : '266'}px)`,
          }}
        >
          <Pagination
            showSizeChanger
            current={pageMeta.page}
            total={pageMeta.queryCount}
            pageSize={pageMeta.pageSize}
            onChange={changePage}
          />
        </div>
      )}
    </div>
  )
}
